/**
 * Validation for a save an artifact asks the app to perform (APP-3495).
 *
 * This runs on the side that actually writes the file, which is why the rules live here rather than
 * on the server: pi-server injects the shim that sends the request but validates nothing about the
 * payload. Everything in the message is agent-authored, including the fields that describe it, so
 * nothing here trusts a claim it can check against the bytes.
 *
 * See pi-server docs/adrs/0015 and docs/designs/artifact-brokered-download.md.
 */

/** The frame document itself is capped at 8 MB, but a wide CSV export legitimately exceeds that. */
export const MAX_SAVE_BYTES = 25 * 1024 * 1024

const MESSAGE_SOURCE = "op-artifact"
const MESSAGE_VERSION = 1
const MESSAGE_TYPE = "save-file"
const UNBROKERED_TYPE = "unbrokered"

/**
 * Allowlist, so every format nobody thought about is refused rather than permitted. `text/html` and
 * `image/svg+xml` are absent deliberately — both run script when a saved copy is opened from disk.
 * The extension comes from this table and never from the filename the artifact asked for.
 */
const EXTENSION_BY_MEDIA_TYPE: Record<string, string> = {
  "text/csv": "csv",
  "text/plain": "txt",
  "application/json": "json",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  // A PDF reader can run script in a saved file, but only inside the reader's own sandbox — no DOM, no
  // file:// origin, nothing of the app. That is the line: `text/html` and `image/svg+xml` open in the
  // browser as a local document and are excluded for it. Included because a shipped skill exports one.
  "application/pdf": "pdf",
}

const MAX_FILENAME_BASE = 100
const DEFAULT_BASENAME = "export"

/**
 * C0 controls, DEL, and the characters Windows and macOS reject outright — plus the Unicode
 * characters that are invisible or reorder the text that follows them. The bidi marks, embeddings,
 * overrides and isolates are here because the confirmation dialog shows this name and nothing else
 * tells the user what they are agreeing to: a claimed `data<U+202E>gnp.exe` on a `text/csv` payload
 * is written as `.csv` but reads to the user as `datavsc.png`, and the override reorders the type and
 * size in the same sentence.
 *
 * Spelled with escapes on purpose: the same class written with literal bytes is invisible in an
 * editor, and a stray reformat would silently destroy it while every test kept passing.
 */
// eslint-disable-next-line no-control-regex
const UNSAFE_FILENAME_CHARS = /[\x00-\x1f\x7f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff<>:"|?*]/g

export type RefusalReason = "shape" | "media-type" | "too-large"

export interface PendingSave {
  filename: string
  contentType: string
  sizeBytes: number
  blob: Blob
}

/**
 * What one prompt covers. A batch export is a single button in a shipped skill that fires one request
 * per file a fifth of a second apart, so the unit the user decides on is the set, not the message.
 */
export interface PendingBatch {
  files: PendingSave[]
  totalBytes: number
}

export type SaveRequestResult =
  | { ok: true, save: PendingSave }
  | { ok: false, reason: RefusalReason }

function refuse(reason: RefusalReason): SaveRequestResult {
  return { ok: false, reason }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function mediaTypeOf(blob: Blob): string {
  return typeof blob.type === "string" ? blob.type.split(";")[0].trim().toLowerCase() : ""
}

/**
 * A name safe to hand to a download attribute: basename only, no control, invisible, reordering or
 * reserved characters, not hidden, length-capped, and carrying the extension the validated media type
 * dictates. A claimed `report.csv.exe` on a `text/csv` payload becomes `report.csv`.
 */
function safeFilename(claimed: unknown, extension: string): string {
  const basename = (typeof claimed === "string" ? claimed : "").split(/[/\\]/).pop() ?? ""
  // Leading dots and whitespace come off together, in one pass: stripping dots first left `" .bashrc"`
  // hidden, because the anchor never reached the dot past the space.
  const cleaned = basename.replace(UNSAFE_FILENAME_CHARS, "").replace(/^[\s.]+/, "").trim()

  let name = cleaned === "" ? DEFAULT_BASENAME : cleaned
  if (name.length > MAX_FILENAME_BASE) name = name.slice(0, MAX_FILENAME_BASE)

  const suffix = `.${extension}`
  if (!name.toLowerCase().endsWith(suffix)) {
    // One level only, so a mismatched extension is replaced rather than stacked on top of.
    name = name.replace(/\.[A-Za-z0-9]{1,8}$/, "")
    if (name === "") name = DEFAULT_BASENAME
    if (!name.toLowerCase().endsWith(suffix)) name += suffix
  }
  return name
}

/**
 * The shim recognised a download attempt but could not resolve its bytes — the sign that a skill has
 * drifted off the interception path (APP-3535). It carries no payload: there is nothing to save and
 * nothing about it worth trusting, only the fact that an export was attempted and failed.
 */
export function isUnbrokeredReport(data: unknown): boolean {
  return isRecord(data)
    && data.source === MESSAGE_SOURCE
    && data.version === MESSAGE_VERSION
    && data.type === UNBROKERED_TYPE
}

export function parseSaveRequest(data: unknown): SaveRequestResult {
  if (!isRecord(data)) return refuse("shape")
  if (data.source !== MESSAGE_SOURCE || data.version !== MESSAGE_VERSION || data.type !== MESSAGE_TYPE) {
    return refuse("shape")
  }

  const { blob } = data
  if (!(blob instanceof Blob)) return refuse("shape")

  // The Blob's own type, not the message's contentType: both are artifact-controlled, so trusting
  // the label over the payload would let an artifact save anything by mislabelling it.
  const contentType = mediaTypeOf(blob)
  // Own properties only. `Blob` does not validate its type beyond lowercasing and an ASCII range
  // check, so `blob.type` can be `constructor` or `__proto__` — a plain `[contentType]` lookup reaches
  // Object.prototype, returns something defined, and walks straight past the allowlist.
  if (!Object.hasOwn(EXTENSION_BY_MEDIA_TYPE, contentType)) return refuse("media-type")
  const extension = EXTENSION_BY_MEDIA_TYPE[contentType]

  if (blob.size > MAX_SAVE_BYTES) return refuse("too-large")

  return {
    ok: true,
    save: { filename: safeFilename(data.filename, extension), contentType, sizeBytes: blob.size, blob },
  }
}
